import { type MutableRefObject, useCallback, useRef } from 'react';
import { getAttributeForSave } from 'utils/agendaRecord/agendaRecord';
import type { TableRef } from 'components/common/Table/Table';
import type { AttributeInput } from 'utils/table/user/contextMenu';
import type { FormItemDefinition } from 'types';

interface UsePendingRowsSaveProps {
  tableRef: MutableRefObject<TableRef | null>;
  dataSchema: Record<string, { formItemDefinition?: FormItemDefinition }>;
  handleOnSaveEdit: (tableRef: MutableRefObject<TableRef | null>) => Promise<void>;
}

/**
 * Tracks the latest rows save so button actions can wait for it — a button click blurs
 * the edited cell first, and the action must run only after that save has committed.
 * The ref is kept after settlement: a save that fails before the action checks would
 * otherwise be cleared already, and its rejection would never reach waitForPendingSave.
 */
export const usePendingRowsSave = ({ tableRef, dataSchema, handleOnSaveEdit }: UsePendingRowsSaveProps) => {
  const pendingSaveRef = useRef<Promise<void> | null>(null);

  const saveRows = useCallback((): Promise<void> => {
    const save = (pendingSaveRef.current ?? Promise.resolve()).catch(() => undefined).then(() => handleOnSaveEdit(tableRef));

    pendingSaveRef.current = save;

    return save;
  }, [handleOnSaveEdit, tableRef]);

  const waitForPendingSave = useCallback((): Promise<void> => pendingSaveRef.current ?? Promise.resolve(), []);

  const getLiveRowAttributes = useCallback(
    (rowId: string | undefined): AttributeInput[] | undefined => {
      if (!rowId) return undefined;

      const rowValues = tableRef.current?.getValues()?.[rowId];
      if (!rowValues || typeof rowValues !== 'object') return undefined;

      return Object.entries(rowValues)
        .map(([itemId, cellValue]): AttributeInput | undefined => {
          const formItemDefinition = dataSchema[itemId]?.formItemDefinition;
          if (!formItemDefinition?.type) return undefined;
          return getAttributeForSave(formItemDefinition.type, itemId, cellValue, formItemDefinition);
        })
        .filter((attribute): attribute is AttributeInput => Boolean(attribute));
    },
    [dataSchema, tableRef]
  );

  return { saveRows, waitForPendingSave, getLiveRowAttributes };
};
